import type { FilterControlContext } from '@clockwise/report-kit';
import type { Dimension, DimensionOption } from '@clockwise/semantic-core';
import { filterOptions } from '@clockwise/semantic-core';
import { FilterChipControl, FilterChipLabel, FilterOperatorSwitch } from '@clockwise/ui';
import { useMemo } from 'react';
import { EntityPicker } from '@/components/custom/entity-picker';
import type { EntityPickerConfig } from '@/components/custom/entity-pickers';
import { analyticsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * The real value picker for a report's filter chip.
 *
 * This is the app half of `report-kit`'s `renderFilterControl` seam. The
 * library draws the bar, owns which filters are on it and holds their values;
 * what it cannot do is FETCH — it knows nothing about this application's API,
 * its auth or its query client — so a filter over Customer or Employee had
 * nothing to offer but a free-text box asking for a uuid. That is the whole
 * reason the filters read as broken: they worked, and there was no way to give
 * them a value anybody could type.
 *
 * It is the SHARED `EntityPicker`, not a picker of its own. Search-as-you-type,
 * the selected-first ordering, the chips, the "first 500 of 1,340" footer and
 * the keyboard handling are already solved for every other picker in the
 * product, and a report filter behaving differently from the customer filter on
 * the customers list would be a second answer to the same question. All this
 * file supplies is the config: where the options come from.
 *
 * WHERE THEY COME FROM, and why it is not `/api/customers`. The members are
 * read from the REPORT's own rows — see `AnalyticsService.dimensionValues` —
 * which buys two things at once. Visibility is inherited rather than
 * re-implemented: the same SELECT that decides which rows a scoped caller may
 * see decides which customers their filter offers, so the two cannot drift.
 * And the list is the set of values that would actually change the report; the
 * customer catalog would mostly offer choices that empty it.
 */
export function reportFilterControl(context: FilterControlContext) {
  // Only `'distinct'` needs this app. A dimension whose vocabulary the model
  // states outright is served by the bar's own chip with no round trip, and one
  // with no enumerable members has nothing to fetch — returning null there
  // keeps the built-in control rather than replacing it with a worse one.
  if (filterOptions(context.filter) !== 'distinct') return null;
  return <ReportFilterPicker {...context} />;
}

/**
 * How much of a dimension's vocabulary one panel holds.
 *
 * The endpoint's own ceiling, and the same page the customer and employee
 * pickers ask for. A short page is what makes a live value look absent, so the
 * page is as large as the server will serve and anything past it is called out
 * by the picker's truncation footer rather than left silent.
 */
const MEMBER_PAGE = 500;

function ReportFilterPicker({
  filter,
  model,
  label,
  hint,
  values,
  onChange,
  opLabel,
  operatorSwitch,
  openOnMount,
  anchorRef,
  onClose,
  from,
  to,
}: FilterControlContext) {
  const config = useMemo(
    () => memberConfig(model.id, filter, from, to),
    [model.id, filter, from, to],
  );

  return (
    <>
      {/* The dimension's name, beside its values. A bare picker would show only
          the chosen names, so two filters side by side would give the reader no
          way to tell which column each one narrows.

          The name and the box around the picker are the SHARED chip's pieces,
          not this file's — and the dashboard mounts the same bar, so a
          Customer filter reads and measures the same on both surfaces. The chip
          shell, its pin and its ✕ are drawn by `report-kit`; everything inside
          it is here, because only the app knows how to fetch a member list.
          `label` rather than `filter.label`: a dashboard's author may have
          renamed the chip to tell two alike ones apart.

          With the operator, because the chip has no operator control of its
          own: "Customer is not" is the only place on the bar an exclusion
          shows. */}
      <FilterChipLabel name={`${label} ${opLabel}`.trim()} hint={hint} />
      <FilterChipControl>
        <EntityPicker
          config={config}
          multiple
          value={values}
          onChange={onChange}
          // is / is not, at the top of the panel the reader already opened.
          header={
            operatorSwitch ? (
              <FilterOperatorSwitch {...operatorSwitch} label={`${label} operator`} />
            ) : undefined
          }
          // The chip was added a click ago and choosing a value is the point of
          // having added it. See `FilterControlContext.openOnMount`.
          defaultOpen={openOnMount}
          // Dismissed with nothing chosen, the chip comes off the bar. See
          // `FilterControlContext.onClose`.
          onOpenChange={(open) => {
            if (!open) onClose();
          }}
          // The panel lines up with the CHIP, not with this control — which
          // starts after the dimension's name. See `FilterControlContext.anchorRef`.
          anchorRef={anchorRef}
          // No placeholder: an unfilled chip reads as the dimension's name alone,
          // and it is a state the reader is never left sitting on — the bar takes
          // the chip off when its panel closes empty.
          placeholder=""
          searchPlaceholder={`Search ${label.toLowerCase()}…`}
          // Many short chips in a row, in a bar that sits above the report: let
          // the chip area scroll rather than push the preview down the page.
          badgeOverflow="scroll"
          popoverWidth="auto"
        />
      </FilterChipControl>
    </>
  );
}

/**
 * The fetch + cache contract for one dimension's members.
 *
 * PERIOD-KEYED throughout. The members of a dimension are the values it takes
 * over a window, and for a scoped caller the window also decides what they may
 * see — who led which customer is a question about a date range. So the period
 * is part of every cache key, and changing the report's dates re-asks rather
 * than serving the previous month's answer.
 *
 * EXPORTED for any other surface that needs one dimension's members.
 * A dashboard filtering Customer differently from a report filtering Customer —
 * a different list, a different page size, a different notion of which members
 * a scoped caller may see — would be a second answer to one question, and the
 * visibility half of that answer is not one to have twice.
 */
export function memberConfig(
  modelId: string,
  dimension: Dimension,
  from: string,
  to: string,
): EntityPickerConfig<DimensionOption> {
  return {
    entityLabel: dimension.label,
    fetchList: ({ q, limit }) =>
      analyticsApi.dimensionValues(modelId, dimension.id, { from, to, q, limit }),
    fetchOne: batchedLookup(modelId, dimension.id, from, to),
    listKey: ({ q, limit }) =>
      queryKeys.analytics.dimensionValues(modelId, dimension.id, { from, to, q, limit }),
    detailKey: (key) =>
      queryKeys.analytics.dimensionValue(modelId, dimension.id, { from, to, key }),
    getId: (member) => member.key,
    getLabel: (member) => member.label,
    // The page size and the endpoint's ceiling are the same number, so the
    // multi-select's exclude padding cannot ask for one row more than the
    // server accepts. See `EntityPickerConfig.maxLimit`.
    lazyLimit: MEMBER_PAGE,
    maxLimit: MEMBER_PAGE,
  };
}

/**
 * Resolve selected keys into labels — MANY PER REQUEST, not one each.
 *
 * `EntityPicker` resolves its chips through `useQueries`, one query per selected
 * id, and those queries run whenever the picker is mounted rather than only
 * when it is open. Left one-per-call that is a request per value on every
 * report open — and each one takes the endpoint's exact-key path, which
 * deliberately drops the period predicate, so twenty chips meant twenty scans
 * over the tenant's whole timesheet history to render twenty names.
 *
 * The endpoint already takes `keys` as a repeated parameter and resolves the
 * whole set in one statement; nothing was using it. So the batching lives HERE,
 * behind the same `fetchOne(key)` signature the shared config expects: each
 * call parks its key, the first one schedules the flush, and every caller in
 * that tick is answered from the one response. `EntityPicker` is untouched and
 * React Query still gets its own cache entry per key.
 *
 * Per CONFIG, not per module: the closure belongs to one (model, dimension,
 * period), which is exactly the set of keys that may share a request.
 */
export function batchedLookup(
  modelId: string,
  dimensionId: string,
  from: string,
  to: string,
): (key: string) => Promise<{ data: DimensionOption }> {
  let queued: string[] = [];
  let flush: Promise<Map<string, DimensionOption>> | null = null;

  return (key: string) => {
    queued.push(key);
    flush ??= new Promise((resolve, reject) => {
      // A macrotask, not a microtask: `useQueries` starts its queries across
      // effects rather than in one synchronous run, so a microtask would close
      // the batch before the later chips had asked.
      setTimeout(() => {
        const keys = queued;
        queued = [];
        flush = null;
        // CHUNKED at the endpoint's ceiling, which caps both `key` and `limit`.
        // Every chunk is still awaited by every caller, so a batch larger than
        // one request resolves whole — dropping the overflow instead would show
        // raw uuids on exactly the chips a long filter list is made of.
        const chunks: string[][] = [];
        for (let i = 0; i < keys.length; i += MEMBER_PAGE) {
          chunks.push(keys.slice(i, i + MEMBER_PAGE));
        }
        Promise.all(
          chunks.map((chunk) =>
            analyticsApi.dimensionValues(modelId, dimensionId, {
              from,
              to,
              keys: chunk,
              limit: chunk.length,
            }),
          ),
        )
          .then((pages) => resolve(new Map(pages.flatMap((p) => p.data).map((m) => [m.key, m]))))
          .catch(reject);
      }, 0);
    });

    // A key that resolves to nothing is not an error and must not read as one:
    // a saved report can hold a customer who has since logged nothing the
    // report can see. The key is its own label there — unhelpful for a uuid,
    // but intact, selectable and removable, which "Unknown" is not.
    return flush.then((byKey) => ({ data: byKey.get(key) ?? { key, label: key } }));
  };
}
